/**
 * The dictation-engine choice as a hook: which engine is active, whether the
 * user may change it, and how to change it.
 *
 * The listening counterpart of `use-voice-engine-choice.ts`. Simpler than that
 * one, because switching engines here has nothing to cancel: dictation is
 * started by the user holding a key, so there is never a recording in flight at
 * the moment the setting changes in Settings.
 */
import { useCallback, useEffect, useState } from "react";

import {
  isDictationEngineSelectable,
  machineDefaultDictationEngine,
  type DictationEngineChoice,
} from "./dictation-engine-options";
import { isIntelMac } from "@/lib/cpu-arch";
import { useDictationSettingsStore } from "@/stores/dictation-settings-store";

export function useDictationEngineChoice(options?: {
  onChoose?: (engine: DictationEngineChoice) => void;
}) {
  const chosen = useDictationSettingsStore((s) => s.engine);
  const setEngine = useDictationSettingsStore((s) => s.setEngine);
  const onChoose = options?.onChoose;

  // The machine default is a Tauri command, so it cannot be read during
  // render. Until it answers - within a frame or two - assume the platform
  // recogniser, which is also what Rust picks until the voice models are in;
  // an Intel Mac knows at once that it dictates with Whisper.
  const whisperOnly = isIntelMac();
  const [machineDefault, setMachineDefault] = useState<DictationEngineChoice>(
    whisperOnly ? "whisper" : "system",
  );
  const [defaultResolved, setDefaultResolved] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void machineDefaultDictationEngine().then((engine) => {
      if (!cancelled) {
        setMachineDefault(engine);
        setDefaultResolved(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = useCallback(
    (engine: DictationEngineChoice) => {
      setEngine(engine);
      onChoose?.(engine);
    },
    [onChoose, setEngine],
  );

  return {
    /** The engine in effect, honouring the user's choice where there is one -
     *  except on an Intel Mac, where a choice saved before it went Whisper-only
     *  is not one Rust follows either. */
    engine: whisperOnly ? "whisper" : (chosen ?? machineDefault),
    /** Whether to offer the choice at all — see `isDictationEngineSelectable`. */
    isSelectable: isDictationEngineSelectable(),
    /** The persisted choice, or null while following the machine default. */
    chosen,
    resolved: chosen !== null || defaultResolved,
    choose,
  };
}
